import {
    Component,
    ViewChild,
    ElementRef,
    AfterViewInit,
} from '@angular/core';
import { CairoService } from '../fittings/cairo.service';
import { AuthService } from '../auth/auth.service';
import { CairoChartComponent } from '../fittings/chart.component';
import { isBraslabCsv, braslabToWave } from '../fittings/braslab';
import { NgProgress } from 'ngx-progressbar';
import {
    LegacyMachineSelectorComponent,
    LegacyRecipeSelectorComponent,
    LegacySubstrateSelectorComponent,
    LegacySpectrophotometerComponent,
    LegacyTableValuesComponent,
} from './legacy-fittings.controls.component';

/** Original single-column Cairo screen (Bootstrap grid, native selects, top progress bar). */
@Component({
    selector: 'legacy-fittings',
    templateUrl: './legacy-fittings.component.html',
    styleUrls: ['./legacy-fittings.component.css']
})
export class LegacyFittingsComponent implements AfterViewInit {
    private client: string;

    @ViewChild(LegacyMachineSelectorComponent, {static: false}) machineSelector !: LegacyMachineSelectorComponent;
    @ViewChild(LegacyRecipeSelectorComponent, {static: false}) recipeSelector !: LegacyRecipeSelectorComponent;
    @ViewChild(LegacySubstrateSelectorComponent, {static: false}) substrateSelector !: LegacySubstrateSelectorComponent;
    @ViewChild(LegacySpectrophotometerComponent, {static: false}) spectroSelector !: LegacySpectrophotometerComponent;
    @ViewChild(LegacyTableValuesComponent, {static: false}) valuesComponent !: LegacyTableValuesComponent;
    @ViewChild('mainChart', {static: false}) chartComponent: CairoChartComponent;
    @ViewChild('fileLoader', {static: false}) fileLoader: ElementRef;

    constructor(
        private srv: CairoService,
        public auth: AuthService,
        private ngProgress: NgProgress,
    ) {
        this.client = this.auth.shop_id();
    }

    ngAfterViewInit() {
        this.onCustomerSelection(this.client);
    }

    onCustomerSelection(customer: string) {
        this.client = customer;
        if (this.machineSelector) {
            this.machineSelector.load(this.client);
        }
    }

    onMachineSelection(machine: string) {
        this.recipeSelector.load(this.client, machine);
        if (this.spectroSelector) {
            this.spectroSelector.load(this.client, machine);
        }
    }

    onRecipeSelection(recipe: string) {
        let machine = this.machineSelector.value;
        this.substrateSelector.load(this.client, machine, recipe);
        this.valuesComponent.load(this.client, machine, recipe);
    }

    private pad(num: number, size: number): string {
        let s = num + "";
        while (s.length < size) s = "0" + s;
        return s;
    }

    private generateJobInput(): string {
        let content = '';

        let machine = this.machineSelector.value;
        let recipe = this.recipeSelector.value;
        let substrate = this.substrateSelector.value;
        let spectrophotometer: string = this.spectroSelector.value;
        let angle = this.srv.angle(spectrophotometer);
        let reference = this.srv.reference(spectrophotometer);

        content += `machine\t${this.client}${machine}\n`;
        content += `recipe\t${this.client}${machine}_${recipe}\n`;

        content += `substrate\t${substrate}\n`;
        content += `angle\t${angle}\n`;
        content += `reference\t${reference}\n`;

        let layers = this.valuesComponent.get_layers();
        let idxLayer = 0;
        for (; idxLayer < layers.length; idxLayer++) {
            let value = layers[idxLayer];
            if (value) {
                content += `th${this.pad(idxLayer + 1, 2)}\t${value}\n`
            } else {
                content += `th${this.pad(idxLayer + 1, 2)}\tNA\n`
            }
        }
        for (; idxLayer < 15; idxLayer++) {
            content += `th${this.pad(idxLayer + 1, 2)}\tNA\n`
        }

        content += `wave\treflection\n`;
        this.wave.forEach(element => {
            content += `${element[0]}\t${element[1]}\n`;
        });

        return content;
    }

    result = null;
    target_wave = [];
    calculating: boolean = false;

    calculate() {
        this.calculating = true;
        this.ngProgress.set(.05);

        this.srv.doJob(this.generateJobInput())
        .then((response) => {
            if (response.status) {
                this.result = response.body
                this.valuesComponent.show(this.result);

                if (this.result.target_curve) {
                    this.target_wave = this.result.target_curve.map((item) => {
                        return [item.x, item.y];
                    });
                    this.chartComponent.add(this.target_wave);
                }
            }
            this.ngProgress.done();
            this.calculating = false;
        })
        .catch((err) => {
            console.log(err);
            this.ngProgress.done();
            this.calculating = false;
        })
    }

    import() {
        this.fileLoader.nativeElement.click();
    }

    wave = [];

    onFileSelected($event) {
        let files = $event.target.files;
        if (files.length == 1) {
            const fileName = files[0].name;
            let reader = new FileReader();

            reader.onload = (e) => {
                const text = reader.result.toString();

                if (isBraslabCsv(text)) {
                    // Braslab CSV: convert it first (see braslab.ts), then use it as any other curve
                    const converted = braslabToWave(text);
                    if ('error' in converted) {
                        alert(`${fileName}: ${converted.error}`);
                        return;
                    }
                    this.wave = converted.wave;
                } else {
                    // get parsing info
                    let spectrophotometer: string = this.spectroSelector.value;
                    let parsing_info = this.srv.parsing_info(spectrophotometer);

                    let lines: string[] = text.split('\n');

                    // remove header
                    lines = lines.slice(parsing_info.header);

                    // filter empty lines
                    lines = lines.filter(line => line.length > 0);

                    // split columns
                    let dataStr = lines.map(item => item.split(parsing_info.separator));

                    // parse numbers
                    this.wave = dataStr.map((item) => {
                        return [
                            parseFloat(item[0].replace(',', '.')),
                            parseFloat(item[1].replace(',', '.'))
                        ];
                    });
                }

                this.chartComponent.load(this.wave);
            }

            // Braslab CSVs come in Windows-1252 (accents in their preamble)
            reader.readAsText(files[0], /\.csv$/i.test(fileName) ? 'windows-1252' : undefined);
        }
    }
};
